import React, { createContext, useContext, useEffect, useState, useMemo } from 'react'
import { useCustomFetch } from '../hooks/useCustomFetch'
import { AuthContext } from './AuthContext'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

interface RoadmapContextType {
  products: any[]
  setProducts: React.Dispatch<React.SetStateAction<any[]>>
  selectedProduct: any
  setSelectedProduct: React.Dispatch<React.SetStateAction<any>>
  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  isLoadingRoadmap: boolean
  setIsLoadingRoadmap: React.Dispatch<React.SetStateAction<boolean>>
  changeProduct: (product: any) => any
  handleClose: () => void
  allOpportunities: any[]
  roadmapCandidates: any
  meetings: any[]
  changeProductConfigs: (productId: string, configs: any) => any
  toggleArchiveOpportunity: (opportunityId: string) => any
  evidence: any
}

export const RoadmapContext = createContext<RoadmapContextType>({} as RoadmapContextType)

export const RoadmapContextProvider = ({ children }) => {
  const navigate = useNavigate()
  const customFetch = useCustomFetch()
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingRoadmap, setIsLoadingRoadmap] = useState(false)
  const [products, setProducts] = useState<Partial<any>[]>([])
  const [meetings, setMeetings] = useState<Partial<any>[]>([])
  const [selectedProduct, setSelectedProduct] = useState(Array.isArray(products) && products.length ? products[0] : {})
  const [allOpportunities, setAllOpportunities] = useState([])
  const [evidence, setEvidence] = useState({}) // { note_id: note_data }
  const [roadmapCandidates, setRoadmapCandidates] = useState<any>({})
  const { user } = useContext(AuthContext)

  async function setInitialData() {
    try {
      const { products } = await customFetch('/getProducts', 'GET')
      if (!Array.isArray(products)) {
        toast.error('Error fetching products')
        console.error('Error fetching products from RoadmapContext')
        return
      }
      products.sort((a, b) => a.order - b.order)

      // for (let i = 0; i < products.length; i++) {
      //   const product = products[i]
      //   const res = await customFetch(`/getProductSpec/${product.id}`, 'GET')
      //   console.log('res', res)
      //   if (!res.productSpec) {
      //     toast.error('Error fetching products specs')
      //     return
      //   }
      //   products[i].specs = res.productSpec
      // }
      setProducts(products)

      if (products.length) {
        setSelectedProduct(products[0])
        await setNewRoadmap(products[0].id)
      } else {
        setSelectedProduct({})
        setAllOpportunities([])
        setEvidence({})
      }
    } catch (err) {
      console.error('error initiating chat threads!', err)
    }
  }

  async function initializeData() {
    await setInitialData()
    await new Promise((resolve) => setTimeout(resolve, 500))
    setIsLoading(false)
  }

  useEffect(() => {
    if (typeof user !== 'object' || typeof user.workspaces === 'undefined') return

    initializeData()
  }, [user])

  // Create sorted opportunities using useMemo
  const sortedAllOpportunities = useMemo(() => {
    return allOpportunities
  }, [allOpportunities])

  const setNewRoadmap = async (productId: string) => {
    const res = await customFetch(`/getRoadmapCandidates/${productId}`, 'GET')
    setRoadmapCandidates(res.roadmapCandidate)

    // Get meetings for this product to calculate percentages correctly
    const meetingsRes = await customFetch(`/getMeetingsByProductId/${productId}`, 'GET')
    const meetingsForProduct = meetingsRes?.meetings || []
    setMeetings(meetingsForProduct)

    // Sort candidates by percentage of sessions (matching the card display logic)
    res.roadmapCandidate?.candidates?.sort((a, b) => {
      // Calculate distinct meeting count for candidate A
      const evidenceA = Array.isArray(a.Evidence) ? a.Evidence : []
      let evidenceDistinctMeetingsA = [...new Set(evidenceA.map((evidence: any) => evidence.meeting_id))]
      evidenceDistinctMeetingsA = evidenceDistinctMeetingsA.filter((meeting) => meeting)
      const evidenceDistinctMeetingsCountA = evidenceDistinctMeetingsA.length
      const percentageA =
        meetingsForProduct.length > 0
          ? Math.round((evidenceDistinctMeetingsCountA / meetingsForProduct.length) * 100)
          : 0

      // Calculate distinct meeting count for candidate B
      const evidenceB = Array.isArray(b.Evidence) ? b.Evidence : []
      let evidenceDistinctMeetingsB = [...new Set(evidenceB.map((evidence: any) => evidence.meeting_id))]
      evidenceDistinctMeetingsB = evidenceDistinctMeetingsB.filter((meeting) => meeting)
      const evidenceDistinctMeetingsCountB = evidenceDistinctMeetingsB.length
      const percentageB =
        meetingsForProduct.length > 0
          ? Math.round((evidenceDistinctMeetingsCountB / meetingsForProduct.length) * 100)
          : 0

      // Sort by percentage in descending order (highest percentage first)
      return percentageB - percentageA
    })

    setAllOpportunities(res.roadmapCandidate?.candidates || [])
    setEvidence(res.evidence || {})
  }

  async function changeProduct(productId: any) {
    const product = products.find((p) => p.id === productId)
    setSelectedProduct(product)
    setIsLoadingRoadmap(true)
    try {
      await setNewRoadmap(product.id)
    } catch (err) {
      setRoadmapCandidates({})
      setAllOpportunities([])
      console.error('error changing product!', err)
    } finally {
      setIsLoadingRoadmap(false)
    }
  }

  const handleClose = () => {
    navigate('/dashboard/home-new')
  }

  async function changeProductConfigs(productId: string, configs: any) {
    try {
      const res = await customFetch(`/updateProductConfigs/${productId}`, 'PUT', { configs })
      if (!res) {
        toast.error('Error updating product configs')
        return
      }
      setSelectedProduct({ ...selectedProduct, configs })
      setProducts(products.map((product) => (product.id === productId ? { ...product, configs } : product)))
    } catch (err) {
      console.error('error changing product configs!', err)
    }
  }

  async function toggleArchiveOpportunity(opportunityId: string) {
    try {
      const res = await customFetch(`/toggleArchiveOpportunity`, 'PUT', {
        productId: selectedProduct.id,
        opportunityId,
      })
      if (!res) {
        toast.error('Error toggling archive opportunity')
        return
      }
      // Update the raw allOpportunities state - sorting will happen automatically via useMemo
      setAllOpportunities((prevOpportunities) =>
        prevOpportunities.map((opportunity) =>
          opportunity.id === opportunityId ? { ...opportunity, isArchived: !opportunity.isArchived } : opportunity
        )
      )
      setRoadmapCandidates((prevRoadmapCandidates) => ({
        ...prevRoadmapCandidates,
        candidates: prevRoadmapCandidates.candidates.map((candidate) =>
          candidate.id === opportunityId ? { ...candidate, isArchived: !candidate.isArchived } : candidate
        ),
      }))
    } catch (err) {
      console.error('error toggling archive opportunity!', err)
    }
  }

  return (
    <RoadmapContext.Provider
      value={{
        isLoading,
        isLoadingRoadmap,
        products,
        setProducts,
        selectedProduct,
        setSelectedProduct,
        changeProduct,
        setIsLoading,
        setIsLoadingRoadmap,
        handleClose,
        allOpportunities: sortedAllOpportunities,
        roadmapCandidates,
        meetings,
        changeProductConfigs,
        toggleArchiveOpportunity,
        evidence,
      }}
    >
      {children}
    </RoadmapContext.Provider>
  )
}
